/**
 * ConstraintsTab — constraint section for `TableEditor`. Read-only when
 * `editContext` is null (Slice 0.4 scaffolding). When `editContext` is
 * supplied (Phase B1+) the PRIMARY KEY / FOREIGN KEY / UNIQUE / CHECK
 * / Named DEFAULT add+drop affordances open live form popovers wired
 * through their respective `commit*` modules.
 *
 * Layout: ONE ROW PER CONSTRAINT KIND, its Add and Drop side by side. The
 * previous flat strip of ten buttons wrapped into three ragged rows where no
 * Add sat next to its own Drop.
 *
 * Visibility rule: a kind that can NEVER apply on the bound engine is hidden
 * outright; a kind the engine HAS but that this panel can't reach stays
 * visible-and-disabled with its explanatory tooltip (see `DisabledAffordance`,
 * which surfaces that copy through a real hover target). Named DEFAULT objects
 * are the only kind in the first bucket — they are a SQL Server-specific
 * concept with no counterpart on the other engines, so they only render on
 * mssql (and in the engine-less read-only scaffolding, where there is nothing
 * to gate on). SQLite's PK / FK / UNIQUE / CHECK stay visible-and-disabled:
 * SQLite HAS those constraints, it just can't ALTER them in, and the tooltip
 * names the table-recreate path.
 *
 * This file deliberately stays under the 200-line budget so each
 * subsequent slice can extend a single section without forcing a
 * refactor.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 (read-only) + § Slice B1 (PK)
 *      + § Slice B2 (FK) + § Slice B3 (UNIQUE) + § Slice B4 (CHECK)
 *      + § Slice B5 (Named DEFAULT).
 */
import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import type { VqeDdlEditContext } from '../../edit/editContext';
import {
  CheckAddPopover,
  CheckDropPopover,
} from '../common/CheckPopovers';
import { DisabledAffordance } from '../common/DisabledAffordance';
import {
  ForeignKeyAddPopover,
  ForeignKeyDropPopover,
} from '../common/ForeignKeyPopovers';
import {
  NamedDefaultAddPopover,
  NamedDefaultDropPopover,
} from '../common/NamedDefaultPopovers';
import {
  PrimaryKeyAddPopover,
  PrimaryKeyDropPopover,
} from '../common/PrimaryKeyPopovers';
import {
  UniqueAddPopover,
  UniqueDropPopover,
} from '../common/UniquePopovers';

const ADD_PK_TOOLTIP = 'Connect an edit context to add a primary key';
const ADD_FK_TOOLTIP = 'Connect an edit context to add a foreign key';
const ADD_UQ_TOOLTIP = 'Connect an edit context to add a unique constraint';
const ADD_CK_TOOLTIP = 'Connect an edit context to add a check constraint';
const ADD_DF_TOOLTIP = 'Connect an edit context to add a named default';

/** `DdlStructure` carries no structured constraint list — table-level
 *  constraints land inside `create-table.opaqueRanges` / `alter-table`'s
 *  `opaque` clauses — so there is nothing to enumerate here yet. */
const NO_CONSTRAINT_LIST_HINT =
  'Existing constraints aren’t listed yet — add or drop one by kind above.';

export interface ConstraintsTabProps {
  /** Required `data-testid` prefix for unit + E2E specs. */
  readonly testId?: string;
  /** Optional edit context — when supplied, the per-kind add/drop affordances
   *  open live form popovers. When null/undefined, every affordance stays
   *  disabled (Slice 0.4 scaffolding). */
  readonly editContext?: VqeDdlEditContext | null;
  /** Compact density (compact Visual DDL popover): smaller affordance buttons,
   *  a tighter placeholder block, and no section label (the popover's tab strip
   *  right above already says “Constraints”). */
  readonly compact?: boolean;
}

interface KindRowProps {
  readonly testId: string;
  readonly kind: string;
  readonly children: ReactNode;
}

function KindRow({ testId, kind, children }: KindRowProps) {
  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-testid={`${testId}-kind-${kind}`}
    >
      {children}
    </div>
  );
}

export function ConstraintsTab({
  testId = 'ddl-constraints-tab',
  editContext,
  compact = false,
}: ConstraintsTabProps) {
  const affordanceClass = compact
    ? 'h-6 gap-1 px-1.5 text-xs text-muted-foreground'
    : undefined;
  const engine = editContext?.service.engineId ?? null;
  const showNamedDefault = engine === null || engine === 'mssql';

  return (
    <div
      className={cn('flex flex-col', compact ? 'gap-2' : 'gap-3')}
      data-testid={testId}
    >
      {compact ? null : (
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Constraints
        </div>
      )}

      <div className="flex flex-col gap-1" data-testid={`${testId}-kinds`}>
        <KindRow testId={testId} kind="pk">
          {editContext ? (
            <PrimaryKeyAddPopover editContext={editContext} testId={`${testId}-add-pk`} />
          ) : (
            <DisabledAffordance
              testId={`${testId}-add-pk`}
              tooltip={ADD_PK_TOOLTIP}
              variant="ghost"
              className={affordanceClass}
            >
              <Plus className="h-3.5 w-3.5" />
              Primary key
            </DisabledAffordance>
          )}
          {editContext ? (
            <PrimaryKeyDropPopover editContext={editContext} testId={`${testId}-drop-pk`} />
          ) : null}
        </KindRow>

        <KindRow testId={testId} kind="fk">
          {editContext ? (
            <ForeignKeyAddPopover editContext={editContext} testId={`${testId}-add-fk`} />
          ) : (
            <DisabledAffordance
              testId={`${testId}-add-fk`}
              tooltip={ADD_FK_TOOLTIP}
              variant="ghost"
              className={affordanceClass}
            >
              <Plus className="h-3.5 w-3.5" />
              Foreign key
            </DisabledAffordance>
          )}
          {editContext ? (
            <ForeignKeyDropPopover editContext={editContext} testId={`${testId}-drop-fk`} />
          ) : null}
        </KindRow>

        <KindRow testId={testId} kind="unique">
          {editContext ? (
            <UniqueAddPopover editContext={editContext} testId={`${testId}-add-unique`} />
          ) : (
            <DisabledAffordance
              testId={`${testId}-add-unique`}
              tooltip={ADD_UQ_TOOLTIP}
              variant="ghost"
              className={affordanceClass}
            >
              <Plus className="h-3.5 w-3.5" />
              Unique
            </DisabledAffordance>
          )}
          {editContext ? (
            <UniqueDropPopover editContext={editContext} testId={`${testId}-drop-unique`} />
          ) : null}
        </KindRow>

        <KindRow testId={testId} kind="check">
          {editContext ? (
            <CheckAddPopover editContext={editContext} testId={`${testId}-add-check`} />
          ) : (
            <DisabledAffordance
              testId={`${testId}-add-check`}
              tooltip={ADD_CK_TOOLTIP}
              variant="ghost"
              className={affordanceClass}
            >
              <Plus className="h-3.5 w-3.5" />
              Check
            </DisabledAffordance>
          )}
          {editContext ? (
            <CheckDropPopover editContext={editContext} testId={`${testId}-drop-check`} />
          ) : null}
        </KindRow>

        {showNamedDefault ? (
          <KindRow testId={testId} kind="named-default">
            {editContext ? (
              <NamedDefaultAddPopover
                editContext={editContext}
                testId={`${testId}-add-named-default`}
              />
            ) : (
              <DisabledAffordance
                testId={`${testId}-add-named-default`}
                tooltip={ADD_DF_TOOLTIP}
                variant="ghost"
                className={affordanceClass}
              >
                <Plus className="h-3.5 w-3.5" />
                Named default
              </DisabledAffordance>
            )}
            {editContext ? (
              <NamedDefaultDropPopover
                editContext={editContext}
                testId={`${testId}-drop-named-default`}
              />
            ) : null}
          </KindRow>
        ) : null}
      </div>

      <div
        className={cn(
          'rounded-md border border-dashed border-border text-center text-xs text-muted-foreground',
          compact ? 'px-2 py-3' : 'px-3 py-6',
        )}
        data-testid={`${testId}-empty`}
      >
        {NO_CONSTRAINT_LIST_HINT}
      </div>
    </div>
  );
}
